import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/inherits";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
// Copyright 1999-2020. Plesk International GmbH. All rights reserved.
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Dialog from '../Dialog';
import { CLS_PREFIX } from '../../constants';
import Translate from '../Translate';
import { safeInvoke } from '../utils';
import { STATUS_DONE, STATUS_ERROR } from '../ProgressStep';
import Progress from '../Progress';

/**
 * `ProgressDialog` is a [Dialog](#!/Dialog) combined with of [Progress](#!/Progress) component.
 * @since 1.2.0
 */
var ProgressDialog =
/*#__PURE__*/
function (_Component) {
  _inherits(ProgressDialog, _Component);

  function ProgressDialog() {
    var _getPrototypeOf2;

    var _this;

    _classCallCheck(this, ProgressDialog);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _possibleConstructorReturn(this, (_getPrototypeOf2 = _getPrototypeOf(ProgressDialog)).call.apply(_getPrototypeOf2, [this].concat(args)));

    _defineProperty(_assertThisInitialized(_this), "state", {
      isOpen: false,
      isFinished: false,
      isFailed: false
    });

    _defineProperty(_assertThisInitialized(_this), "closeTimeout", null);

    _defineProperty(_assertThisInitialized(_this), "handleStatusChange", function (status) {
      _this.setState({
        isFinished: status === STATUS_DONE,
        isFailed: status === STATUS_ERROR
      });
    });

    _defineProperty(_assertThisInitialized(_this), "handleClose", function () {
      var _this$props = _this.props,
          onClose = _this$props.onClose,
          onCancel = _this$props.onCancel,
          onFinish = _this$props.onFinish,
          onFail = _this$props.onFail,
          cancelable = _this$props.cancelable;
      var _this$state = _this.state,
          isOpen = _this$state.isOpen,
          isFinished = _this$state.isFinished,
          isFailed = _this$state.isFailed;
      clearTimeout(_this.closeTimeout);

      if (!isOpen) {
        return;
      }

      if (isFinished) {
        safeInvoke(onFinish);
      } else if (isFailed) {
        safeInvoke(onFail);
      } else if (cancelable) {
        safeInvoke(onCancel);
      }

      _this.setState({
        isOpen: false
      }, function () {
        return safeInvoke(onClose);
      });
    });

    return _this;
  }

  _createClass(ProgressDialog, [{
    key: "componentDidUpdate",
    value: function componentDidUpdate(prevProps, prevState) {
      var isFinished = this.state.isFinished;
      var autoCloseTimeout = this.props.autoCloseTimeout;

      if (prevState.isFinished === false && isFinished === true) {
        clearTimeout(this.closeTimeout);

        if (autoCloseTimeout) {
          this.closeTimeout = setTimeout(this.handleClose, autoCloseTimeout);
        }
      }
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      clearTimeout(this.closeTimeout);
    }
  }, {
    key: "render",
    value: function render() {
      var _this$props2 = this.props,
          baseClassName = _this$props2.baseClassName,
          className = _this$props2.className,
          children = _this$props2.children,
          description = _this$props2.description,
          cancelable = _this$props2.cancelable,
          autoCloseTimeout = _this$props2.autoCloseTimeout,
          onCancel = _this$props2.onCancel,
          onClose = _this$props2.onClose,
          onFinish = _this$props2.onFinish,
          onFail = _this$props2.onFail,
          props = _objectWithoutProperties(_this$props2, ["baseClassName", "className", "children", "description", "cancelable", "autoCloseTimeout", "onCancel", "onClose", "onFinish", "onFail"]);

      var _this$state2 = this.state,
          isOpen = _this$state2.isOpen,
          isFinished = _this$state2.isFinished,
          isFailed = _this$state2.isFailed;
      var canClose = isFinished || isFailed;
      var canCancel = cancelable && !canClose;
      var cancelTitle = canClose ? React.createElement(Translate, {
        content: "ProgressDialog.closeButton",
        fallback: "Close"
      }) : React.createElement(Translate, {
        content: "ProgressDialog.cancelButton",
        fallback: "Cancel"
      });
      return React.createElement(Dialog, _extends({
        className: classNames(baseClassName, className),
        size: "xs",
        cancelTitle: cancelTitle
      }, props, {
        isOpen: isOpen,
        onClose: this.handleClose,
        closable: canClose || canCancel
      }), description && React.createElement("div", {
        className: "".concat(baseClassName, "__description")
      }, description), React.createElement(Progress, {
        className: "".concat(baseClassName, "__content"),
        onStatusChange: this.handleStatusChange
      }, children));
    }
  }], [{
    key: "getDerivedStateFromProps",
    value: function getDerivedStateFromProps(_ref) {
      var isOpen = _ref.isOpen;
      return {
        isOpen: isOpen
      };
    }
  }]);

  return ProgressDialog;
}(Component);

ProgressDialog.propTypes = {
  /**
   * Toggles visibility.
   * @since 1.2.0
   */
  isOpen: PropTypes.bool,

  /**
   * Banner. Renders in front of content as wide as dialog.
   * @since 1.2.0
   */
  banner: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.element]),

  /**
   * Image. Renders into content.
   * @since 1.2.0
   */
  image: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.element]),

  /**
   * Title
   * @since 1.2.0
   */
  title: PropTypes.node,

  /**
   * Description of the `ProgressDialog`.
   * @since 1.2.0
   */
  description: PropTypes.node,

  /**
   * Actions which will be shown in Dialog header.
   * @since 1.2.0
   */
  actions: PropTypes.node,

  /**
   * Whether the dialog should be canceled with the cancel button.
   * @since 1.2.0
   */
  cancelable: PropTypes.bool,

  /**
   * The interval of time to auto close dialog when it successful finished.
   * Set `null` for disable auto close.
   * @since 1.2.0
   */
  autoCloseTimeout: PropTypes.number,

  /**
   * On cancel event handler.
   * @since 1.2.0
   */
  onCancel: PropTypes.func,

  /**
   * On close event handler.
   * @since 1.2.0
   */
  onClose: PropTypes.func,

  /**
   * On finish event handler.
   * @since 1.2.0
   */
  onFinish: PropTypes.func,

  /**
   * On fail event handler.
   * @since 1.2.0
   */
  onFail: PropTypes.func,

  /**
   * Content of the `ProgressDialog`.
   * @since 1.2.0
   */
  children: PropTypes.node,

  /**
   * @ignore
   */
  className: PropTypes.string,

  /**
   * @ignore
   */
  baseClassName: PropTypes.string
};
ProgressDialog.defaultProps = {
  isOpen: false,
  banner: null,
  image: null,
  title: null,
  description: null,
  actions: null,
  cancelable: false,
  autoCloseTimeout: 3000,
  onCancel: null,
  onClose: null,
  onFinish: null,
  onFail: null,
  children: null,
  className: null,
  baseClassName: "".concat(CLS_PREFIX, "progress-dialog")
};
export default ProgressDialog;